import { useState, useEffect } from 'react';
import { Building, Anchor } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

export default function SmartMoneyTracker() {
  const [tab, setTab] = useState('institutional');
  const [smartMoney, setSmartMoney] = useState([]);
  const [onChain, setOnChain] = useState({ whaleTransactions: [], exchangeFlows: [] });

  useEffect(() => {
    async function fetchData() {
      try {
        const [smRes, ocRes] = await Promise.all([
          fetch('/api/signals/smart-money'),
          fetch('/api/signals/on-chain'),
        ]);
        if (smRes.ok) setSmartMoney(await smRes.json());
        if (ocRes.ok) setOnChain(await ocRes.json());
      } catch { /* silent */ }
    }
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ModuleContainer title="Smart Money" subtitle="Institutional flows + on-chain whale activity">
      {/* Tab selector */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'institutional', label: 'Institutional (13F)', icon: Building },
          { key: 'onchain', label: 'On-Chain Whales', icon: Anchor },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-mono font-bold ${
              tab === key ? 'bg-korinth-accent/10 text-korinth-accent border border-korinth-accent' : 'bg-korinth-bg text-korinth-muted border border-korinth-border'
            }`}
          >
            <Icon className="w-3 h-3" />{label}
          </button>
        ))}
      </div>

      {tab === 'institutional' ? (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {smartMoney.length > 0 ? smartMoney.map((m, i) => (
            <div key={m.id || i} className="flex items-center justify-between px-3 py-2 bg-korinth-bg rounded text-xs font-mono">
              <div className="flex items-center gap-2">
                <Building className="w-3 h-3 text-indigo-400" />
                <span className="text-white">{m.entity}</span>
                <span className={`text-[9px] px-1 py-0.5 rounded ${
                  m.action === 'BUY' ? 'bg-korinth-accent/10 text-korinth-accent' : m.action === 'SELL' ? 'bg-korinth-danger/10 text-korinth-danger' : 'bg-korinth-border text-korinth-muted'
                }`}>{m.action}</span>
              </div>
              <div className="text-right">
                {m.asset && <span className="text-korinth-text">{m.asset}</span>}
                <span className="text-korinth-muted ml-2">{m.filing_date?.slice(0, 10)}</span>
              </div>
            </div>
          )) : (
            <p className="text-xs text-korinth-muted font-mono text-center py-6">No 13F data yet. Checked daily at 10:00 Dubai.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Exchange flow bars */}
          {onChain.exchangeFlows.length > 0 && (
            <div>
              <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Net Exchange Flows (24h)</h4>
              <div className="space-y-1.5">
                {[...new Map(onChain.exchangeFlows.map(f => [f.symbol, f])).values()].map((f, i) => {
                  const maxFlow = Math.max(...onChain.exchangeFlows.map(x => Math.abs(x.net_flow_usd || 0)), 1);
                  const pct = Math.abs(f.net_flow_usd || 0) / maxFlow * 100;
                  const isOutflow = (f.net_flow_usd || 0) < 0;
                  return (
                    <div key={f.symbol || i} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-korinth-muted w-10">{f.symbol}</span>
                      <div className="flex-1 h-3 bg-korinth-bg rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${isOutflow ? 'bg-korinth-accent' : 'bg-korinth-danger'}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                      <span className={`text-[10px] font-mono w-20 text-right ${isOutflow ? 'text-korinth-accent' : 'text-korinth-danger'}`}>
                        {isOutflow ? '▲ OUT' : '▼ IN'} ${Math.abs(f.net_flow_usd / 1e6).toFixed(1)}M
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Whale transactions */}
          <div>
            <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">
              Largest Whale Moves ({onChain.whaleTransactions.length})
            </h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {onChain.whaleTransactions.length > 0 ? onChain.whaleTransactions.slice(0, 15).map((tx, i) => (
                <div key={tx.id || i} className="flex items-center justify-between px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                  <div className="flex items-center gap-1.5">
                    <span className={
                      tx.transaction_type === 'exchange_inflow' ? 'text-korinth-danger' :
                      tx.transaction_type === 'exchange_outflow' ? 'text-korinth-accent' : 'text-korinth-muted'
                    }>
                      {tx.transaction_type === 'exchange_inflow' ? '▼' : tx.transaction_type === 'exchange_outflow' ? '▲' : '→'}
                    </span>
                    <span className="text-white">{tx.symbol}</span>
                  </div>
                  <span className="text-korinth-text">${(tx.amount_usd / 1e6).toFixed(1)}M</span>
                </div>
              )) : (
                <p className="text-[10px] text-korinth-muted text-center py-4">No whale transactions in last 48h</p>
              )}
            </div>
          </div>
        </div>
      )}
    </ModuleContainer>
  );
}
